import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { getSubdomain } from '@/lib/subdomain'

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const subdomain = getSubdomain()
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header subdomain={subdomain} />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  )
}

function Header({ subdomain }: { subdomain: string }) {
  const isLocal = subdomain === 'localhost'
  
  const getLink = (sub: string) => {
    if (isLocal) {
      return `/?subdomain=${sub === 'main' ? '' : sub}`
    }
    return sub === 'main' ? 'https://n0n3.com' : `https://${sub}.n0n3.com`
  }
  
  return (
    <header className="border-b border-gray-800 bg-gray-950/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="text-2xl font-display font-bold text-gradient">n0n3</span>
          {subdomain !== 'main' && subdomain !== 'localhost' && (
            <span className="text-sm text-gray-500 font-medium uppercase tracking-wider">
              {subdomain}
            </span>
          )}
        </Link>
        
        <nav className="flex items-center gap-6">
          <NavLink href={getLink('main')} active={subdomain === 'main' || subdomain === 'localhost'}>
            Home
          </NavLink>
          <NavLink href={isLocal ? '/?subdomain=stats' : getLink('stats')} active={subdomain === 'stats'}>
            Stats
          </NavLink>
          <NavLink href={isLocal ? '/?subdomain=stats&path=/library' : `${getLink('stats')}/library`} active={false}>
            Library
          </NavLink>
          <NavLink href={isLocal ? '/?subdomain=live' : getLink('live')} active={subdomain === 'live'}>
            Live
          </NavLink>
        </nav>
      </div>
    </header>
  )
}

function NavLink({ 
  href, 
  children, 
  active 
}: { 
  href: string
  children: ReactNode
  active?: boolean 
}) {
  const isExternal = href.startsWith('http')
  
  if (isExternal) {
    return (
      <a 
        href={href}
        className={`text-sm font-medium transition-colors ${
          active ? 'text-primary-400' : 'text-gray-400 hover:text-gray-100'
        }`}
      >
        {children}
      </a>
    )
  }
  
  return (
    <Link 
      to={href}
      className={`text-sm font-medium transition-colors ${
        active ? 'text-primary-400' : 'text-gray-400 hover:text-gray-100'
      }`}
    >
      {children}
    </Link>
  )
}

function Footer() {
  return (
    <footer className="border-t border-gray-800 py-8">
      <div className="container mx-auto px-4 text-center text-gray-500 text-sm">
        <p>&copy; {new Date().getFullYear()} n0n3. All rights reserved.</p>
        <p className="mt-2">
          Gaming stats powered by Steam, Epic Games, and Xbox Live APIs.
        </p>
      </div>
    </footer>
  )
}
